import { useCallback, useState } from 'react'
import { toast } from 'react-toastify'
import { mockTransferOut } from './mockTransferOutApi'
import { TransferOutDestination } from './mockTransferOutApi'

interface UseTransferOutOptions {
  projectId: string
  piles: any[]
  setPiles: (piles: any[]) => void
}

export function useTransferOut({ projectId, piles, setPiles }: UseTransferOutOptions) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [destination, setDestination] = useState<TransferOutDestination>('factory')

  const isSelectable = useCallback((row: any) => row.usageStatus === 'received', [])

  const eligibleRows = piles.filter(isSelectable)

  const allEligibleSelected =
    eligibleRows.length > 0 && eligibleRows.every(row => selectedIds.has(String(row.id)))

  const toggleSelect = useCallback(
    (row: any) => {
      if (!isSelectable(row)) return
      const id = String(row.id)
      setSelectedIds(prev => {
        const next = new Set(prev)
        if (next.has(id)) next.delete(id)
        else next.add(id)
        return next
      })
    },
    [isSelectable]
  )

  const toggleSelectAll = useCallback(() => {
    if (allEligibleSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev)
        eligibleRows.forEach(row => next.delete(String(row.id)))
        return next
      })
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev)
        eligibleRows.forEach(row => next.add(String(row.id)))
        return next
      })
    }
  }, [allEligibleSelected, eligibleRows])

  const clearSelection = useCallback(() => setSelectedIds(new Set()), [])

  const openModal = useCallback(() => setIsModalOpen(true), [])
  const closeModal = useCallback(() => {
    if (!isSubmitting) setIsModalOpen(false)
  }, [isSubmitting])

  const submitTransfer = useCallback(async () => {
    const pileIds = Array.from(selectedIds)
    if (!pileIds.length) return

    setIsSubmitting(true)
    try {
      await mockTransferOut(projectId, { pileIds, destination })
      setPiles(
        piles.map(pile =>
          pileIds.includes(String(pile.id)) ? { ...pile, usageStatus: 'returned' } : pile
        )
      )
      clearSelection()
      setIsModalOpen(false)
      toast.success(`ขนส่ง ${pileIds.length} ท่อนกลับโรงงานสำเร็จ`)
    } catch {
      toast.error('ขนส่งไม่สำเร็จ')
    } finally {
      setIsSubmitting(false)
    }
  }, [selectedIds, projectId, destination, piles, setPiles, clearSelection])

  return {
    selectedIds,
    isModalOpen,
    isSubmitting,
    destination,
    setDestination,
    isSelectable,
    allEligibleSelected,
    eligibleCount: eligibleRows.length,
    toggleSelect,
    toggleSelectAll,
    clearSelection,
    openModal,
    closeModal,
    submitTransfer,
  }
}
